import React, { useState, useRef, useEffect, useCallback } from 'react';
import { WorkoutHistory as WorkoutHistoryType, ExerciseDefinition } from '../../../types';
import { apiServices } from '../../../api/services';
import { BaseApiService } from '../../../api/services/BaseApiService';
import { CacheManager } from '../../../api/cache/CacheManager';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../i18n/provider';
import { useI18n } from '../../../i18n/provider';
import styles from './WorkoutHistory.module.css';

interface WorkoutHistoryProps {
  initialWorkouts?: WorkoutHistoryType[]; // Make optional for initial load
  onExerciseClick?: (exercise: WorkoutHistoryType['exercises'][number]) => void;
  onNavigateToExercise?: (exerciseId: string, templateId?: string) => void;
  'data-testid'?: string;
}

type SetInfo = { weight: number; reps: number; unit: string };
type MaxAchieved = { maxWeightSet: SetInfo | null; maxRepsSet: SetInfo | null };

const normalizeExerciseLookup = (value: string): string => value.toLowerCase().trim().replace(/\s+/g, ' ');

const pickBestExerciseMatch = (
  candidates: ExerciseDefinition[],
  requestedName: string
): ExerciseDefinition | null => {
  if (!candidates.length) return null;

  const normalizedRequested = normalizeExerciseLookup(requestedName);

  const exact = candidates.find((candidate) => {
    const normalizedName = normalizeExerciseLookup(candidate.name || '');
    const normalizedLocalizedName = normalizeExerciseLookup(candidate.localizedName || '');
    return normalizedName === normalizedRequested || normalizedLocalizedName === normalizedRequested;
  });

  if (exact) return exact;

  const partial = candidates.find((candidate) => {
    const normalizedName = normalizeExerciseLookup(candidate.name || '');
    const normalizedLocalizedName = normalizeExerciseLookup(candidate.localizedName || '');
    return (
      normalizedName.includes(normalizedRequested) ||
      normalizedRequested.includes(normalizedName) ||
      normalizedLocalizedName.includes(normalizedRequested) ||
      normalizedRequested.includes(normalizedLocalizedName)
    );
  });

  return partial || candidates[0];
};

const getResolvedImageCandidates = (sources: Array<string | undefined>): string[] => {
  const resolved: string[] = [];
  for (const source of sources) {
    if (!source) continue;
    const normalized = BaseApiService.resolveImageUrl(source);
    if (normalized && !resolved.includes(normalized)) {
      resolved.push(normalized);
    }
  }
  return resolved;
};

// Helper function to calculate max achieved for each exercise in a specific workout
const getMaxAchievedForExercise = (
  exerciseName: string, 
  workout: WorkoutHistoryType
): MaxAchieved | null => {
  let maxWeightSet: SetInfo | null = null;
  let maxRepsSet: SetInfo | null = null;
  let foundAny = false;

  // Only look at exercises in the current workout
  workout.exercises.forEach(exercise => {
    if (exercise.name === exerciseName && exercise.completed) {
      foundAny = true;
      
      // Check all sets to find max weight and max reps
      exercise.sets.forEach(set => {
        // Track max weight set
        if (!maxWeightSet || set.weight > maxWeightSet.weight) {
          maxWeightSet = { weight: set.weight, reps: set.reps, unit: set.unit };
        }
        
        // Track max reps set
        if (!maxRepsSet || set.reps > maxRepsSet.reps) {
          maxRepsSet = { weight: set.weight, reps: set.reps, unit: set.unit };
        }
      });
    }
  });

  return foundAny ? { maxWeightSet, maxRepsSet } : null;
};

const WorkoutHistory: React.FC<WorkoutHistoryProps> = ({ 
  initialWorkouts = [], 
  onNavigateToExercise, 
  'data-testid': testId 
}) => {
  const { t } = useTranslation();
  const { locale } = useI18n();
  console.log('🎯 [WORKOUT HISTORY COMPONENT] Component received props:', {
    initialWorkoutsCount: initialWorkouts.length,
    initialWorkoutIds: initialWorkouts.map(w => ({ id: w.id, date: w.date })),
    fullInitialWorkouts: initialWorkouts
  });

  const { user } = useAuth();
  const [expandedExerciseId, setExpandedExerciseId] = useState<string | null>(null);
  const [workouts, setWorkouts] = useState<WorkoutHistoryType[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [exerciseDefsById, setExerciseDefsById] = useState<Record<string, ExerciseDefinition>>({});
  const [failedImageSourcesByExercise, setFailedImageSourcesByExercise] = useState<Record<string, string[]>>({});
  const [editingWorkoutId, setEditingWorkoutId] = useState<string | null>(null);
  const [draftWorkout, setDraftWorkout] = useState<WorkoutHistoryType | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const historyListRef = useRef<HTMLDivElement>(null);
  const hasLoadedInitial = useRef(false);
  const SCROLL_POSITION_KEY = 'workout_history_scroll_position';
  const CACHE_KEY = `workout_history_cache:${locale}`;

  const toDateInputValue = (value: any): string => {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const startEditingWorkout = (workout: WorkoutHistoryType) => {
    setEditingWorkoutId(workout.id);
    setDraftWorkout(JSON.parse(JSON.stringify(workout)) as WorkoutHistoryType);
    setExpandedExerciseId(null);
  };

  const cancelEditingWorkout = () => {
    setEditingWorkoutId(null);
    setDraftWorkout(null);
    setIsSavingEdit(false);
  };

  const saveEditingWorkout = async () => {
    if (!user || !editingWorkoutId || !draftWorkout) return;

    try {
      setIsSavingEdit(true);

      const updated = await apiServices.workoutHistory.updateWorkout(user.id, editingWorkoutId, {
        date: (draftWorkout as any).date,
        exercises: draftWorkout.exercises.map((ex: any) => ({
          exerciseId: (ex as any).exerciseId || ex.id,
          id: ex.id,
          name: ex.name,
          completed: Boolean(ex.completed),
          sets: (ex.sets || []).map((s: any) => ({
            reps: Number(s.reps) || 0,
            weight: Number(s.weight) || 0,
            unit: s.unit,
          })),
        })),
      });

      setWorkouts((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));

      try {
        CacheManager.storage.removeItem(CACHE_KEY);
      } catch {
        // ignore
      }

      cancelEditingWorkout();
    } catch (e: any) {
      alert(e?.message || 'Failed to update workout.');
      setIsSavingEdit(false);
    }
  };

  const updateDraftDate = (nextDateIso: string) => {
    if (!draftWorkout) return;
    setDraftWorkout({
      ...draftWorkout,
      date: new Date(`${nextDateIso}T12:00:00.000Z`) as any,
    });
  };

  const removeDraftExercise = (index: number) => {
    if (!draftWorkout) return;
    const next = [...draftWorkout.exercises];
    next.splice(index, 1);
    setDraftWorkout({ ...draftWorkout, exercises: next, exerciseCount: next.length } as any);
  };

  const updateDraftSet = (exerciseIndex: number, setIndex: number, patch: Partial<{ reps: number; weight: number; unit: any }>) => {
    if (!draftWorkout) return;
    const exercises = [...draftWorkout.exercises] as any[];
    const ex = { ...exercises[exerciseIndex] };
    const sets = [...(ex.sets || [])];
    sets[setIndex] = { ...sets[setIndex], ...patch };
    ex.sets = sets;
    exercises[exerciseIndex] = ex;
    setDraftWorkout({ ...draftWorkout, exercises } as any);
  };

  const addDraftSet = (exerciseIndex: number) => {
    if (!draftWorkout) return;
    const exercises = [...draftWorkout.exercises] as any[];
    const ex = { ...exercises[exerciseIndex] };
    const sets = [...(ex.sets || [])];
    const last = sets[sets.length - 1];
    sets.push({ reps: last?.reps || 0, weight: last?.weight || 0, unit: last?.unit || 'kg' });
    ex.sets = sets;
    exercises[exerciseIndex] = ex;
    setDraftWorkout({ ...draftWorkout, exercises } as any);
  };

  const removeDraftSet = (exerciseIndex: number, setIndex: number) => {
    if (!draftWorkout) return;
    const exercises = [...draftWorkout.exercises] as any[];
    const ex = { ...exercises[exerciseIndex] };
    const sets = [...(ex.sets || [])];
    sets.splice(setIndex, 1);
    ex.sets = sets;
    exercises[exerciseIndex] = ex;
    setDraftWorkout({ ...draftWorkout, exercises } as any);
  };

  // Load cached data on mount
  useEffect(() => {
    if (!user) {
      setWorkouts(initialWorkouts);
      setCurrentPage(1);
      setHasMore(false);
      setInitialLoading(false);
      hasLoadedInitial.current = true;
      return;
    }

    if (hasLoadedInitial.current) return;
    
    const loadCachedData = () => {
      try {
        const cached = CacheManager.storage.getItem(CACHE_KEY);
        if (cached) {
          const { workouts: cachedWorkouts, userId: cachedUserId, timestamp } = JSON.parse(cached);
          
          // Use cache if it's from the same user and less than 5 minutes old
          console.log('🔍 Cache check - Current user:', user.id, 'Cached user:', cachedUserId);
          if (cachedUserId === user.id && Date.now() - timestamp < 300000) {
            console.log('📦 Using cached workout history:', cachedWorkouts.length, 'for user:', user.id);
            setWorkouts(cachedWorkouts);
            setCurrentPage(Math.ceil(cachedWorkouts.length / 8));
            setHasMore(cachedWorkouts.length >= 8);
            setInitialLoading(false);
            hasLoadedInitial.current = true;
            return;
          } else {
            console.log('❌ Cache mismatch - user IDs differ or cache expired, fetching fresh data');
          }
        }
      } catch (error) {
        console.warn('Failed to load cached workout history:', error);
      }
      
      // If no valid cache or cache mismatch, fetch fresh data from backend
      const fetchFreshData = async () => {
        try {
          console.log('🔄 Fetching fresh workout data for user:', user.id);
          const response = await apiServices.workoutHistory.getWorkoutHistory(user.id, 1, 8);
          console.log('📥 Fresh data received:', response.data.length, 'workouts');
          setWorkouts(response.data || []);
          setCurrentPage(1);
          setHasMore(response.data?.length >= 8);

          try {
            const cacheData = {
              workouts: response.data || [],
              userId: user.id,
              timestamp: Date.now(),
            };
            CacheManager.storage.setJson(CACHE_KEY, cacheData);
          } catch (cacheError) {
            console.warn('Failed to cache workout history:', cacheError);
          }
        } catch (error) {
          console.error('Failed to fetch fresh workout data:', error);
          // Fallback to initial workouts if API fails
          console.log('📥 API failed, using initial workouts:', initialWorkouts.length);
          setWorkouts(initialWorkouts);
          setCurrentPage(1);
          setHasMore(initialWorkouts.length >= 8);
        } finally {
          setInitialLoading(false);
          hasLoadedInitial.current = true;
        }
      };
      
      fetchFreshData();
    };
    
    loadCachedData();
  }, [user, initialWorkouts]);

  // Use initialWorkouts prop when provided and user is available
  useEffect(() => {
    if (!user) {
      setWorkouts(initialWorkouts);
      setCurrentPage(1);
      setHasMore(false);
      setInitialLoading(false);
      hasLoadedInitial.current = true;
      return;
    }

    console.log('🔄 [WORKOUT HISTORY COMPONENT] initialWorkouts prop changed:', {
      hasUser: !!user,
      initialWorkoutsCount: initialWorkouts.length,
      shouldUseInitialWorkouts: user && initialWorkouts.length > 0
    });

    if (user && initialWorkouts.length > 0) {
      console.log('✅ [WORKOUT HISTORY COMPONENT] Using initialWorkouts prop instead of fetching fresh data');
      setWorkouts(initialWorkouts);
      setCurrentPage(1);
      setHasMore(initialWorkouts.length >= 8);
      setInitialLoading(false);
      hasLoadedInitial.current = true;
    }
  }, [user, initialWorkouts]); // Add initialWorkouts to dependencies

  // Save to cache whenever workouts change
  useEffect(() => {
    if (workouts.length > 0 && user) {
      try {
        const cacheData = {
          workouts,
          userId: user.id,
          timestamp: Date.now()
        };
        CacheManager.storage.setJson(CACHE_KEY, cacheData);
        console.log('💾 Cached workout history:', workouts.length, 'for user:', user.id);
      } catch (error) {
        console.warn('Failed to cache workout history:', error);
      }
    }
  }, [workouts, user]);

  useEffect(() => {
    setExerciseDefsById({});
    setFailedImageSourcesByExercise({});
  }, [locale]);

  useEffect(() => {
    if (!user || workouts.length === 0) return;

    const uniqueExercises = Array.from(
      new Map(
        workouts
          .flatMap(w => w.exercises)
          .map(ex => {
            const exerciseLookupId = (ex as any).exerciseId || ex.id || '';
            const normalizedName = normalizeExerciseLookup(ex.name || '');
            const key = exerciseLookupId ? `id:${exerciseLookupId}` : `name:${normalizedName}`;
            return [key, { exerciseLookupId, name: ex.name }] as const;
          })
      ).values()
    );

    if (uniqueExercises.length === 0) return;

    const loadExerciseDefs = async () => {
      try {
        const allExercises = await apiServices.exercises.getExercises();
        const indexedById = new Map<string, ExerciseDefinition>();
        const indexedByName = new Map<string, ExerciseDefinition>();
        const next: Record<string, ExerciseDefinition> = {};

        for (const exercise of allExercises) {
          indexedById.set(exercise.id, exercise);
          indexedByName.set(normalizeExerciseLookup(exercise.name), exercise);
          if (exercise.localizedName) {
            indexedByName.set(normalizeExerciseLookup(exercise.localizedName), exercise);
          }

          // Seed the local map from full catalog so history rows can resolve names/images immediately.
          next[exercise.id] = exercise;
          next[normalizeExerciseLookup(exercise.name)] = exercise;
          if (exercise.localizedName) {
            next[normalizeExerciseLookup(exercise.localizedName)] = exercise;
          }
        }

        const missing = uniqueExercises.filter((ex) => {
          const byIdKey = ex.exerciseLookupId ? ex.exerciseLookupId : null;
          const byNameKey = normalizeExerciseLookup(ex.name || '');
          return !(byIdKey && next[byIdKey]) && !next[byNameKey];
        });

        const results = missing.length
          ? await Promise.all(
              missing.map(async ({ exerciseLookupId, name }) => {
            const normalizedName = normalizeExerciseLookup(name || '');

            if (exerciseLookupId && indexedById.has(exerciseLookupId)) {
              return indexedById.get(exerciseLookupId) || null;
            }

            if (indexedByName.has(normalizedName)) {
              return indexedByName.get(normalizedName) || null;
            }

            if (exerciseLookupId) {
              try {
                const def = await apiServices.exercises.getExerciseById(exerciseLookupId);
                if (def) return def;
              } catch {
                // ignore
              }
            }

            try {
              const defByName = await apiServices.exercises.findExerciseByName(name);
              if (defByName) return defByName;
            } catch {
              // ignore and fallback to backend search
            }

            try {
              const searchResults = await apiServices.exercises.searchExercises(name);
              return pickBestExerciseMatch(searchResults, normalizedName);
            } catch {
              return null;
            }
          })
            )
          : [];

        results.forEach((def) => {
          if (!def) return;
          next[def.id] = def;
          next[normalizeExerciseLookup(def.name)] = def;
          if (def.localizedName) {
            next[normalizeExerciseLookup(def.localizedName)] = def;
          }
        });

        setExerciseDefsById(next);
      } catch {
        // ignore
      }
    };

    loadExerciseDefs();
  }, [user, workouts, locale]);

  // Save scroll position when scrolling
  const handleScrollPosition = useCallback(() => {
    const pageContainer = document.querySelector('.page-container') as HTMLElement;
    if (pageContainer) {
      sessionStorage.setItem(SCROLL_POSITION_KEY, pageContainer.scrollTop.toString());
    } else {
      // Fallback to window scroll
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      sessionStorage.setItem(SCROLL_POSITION_KEY, scrollTop.toString());
    }
  }, []);

  // Load more workouts on page scroll
  const handleScrollForPagination = useCallback(async () => {
    if (!user) {
      return;
    }

    // Use page container scroll instead of window
    const pageContainer = document.querySelector('.page-container') as HTMLElement;
    if (!pageContainer) {
      return;
    }
    
    if (loading) {
      return;
    }
    if (!hasMore) {
      return;
    }
    if (workouts.length === 0) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = pageContainer;
    
    // Load more when user is within 100px of the bottom of the container
    if (scrollHeight - scrollTop - clientHeight <= 100) {
      setLoading(true);
      
      try {
        const nextPage = currentPage + 1;
        
        const response = await apiServices.workoutHistory.getWorkoutHistory(user.id, nextPage, 8);
        
        if (response.data.length > 0) {
          setWorkouts(prev => {
            // Filter out any duplicates just in case
            const existingIds = new Set(prev.map(w => w.id));
            const newWorkouts = response.data.filter((w: WorkoutHistoryType) => !existingIds.has(w.id));
            return [...prev, ...newWorkouts];
          });
          setCurrentPage(nextPage);
          // Set hasMore based on the API response
          setHasMore(response.pagination.hasNext);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error('Error loading more workouts:', error);
      } finally {
        setLoading(false);
      }
    }
  }, [currentPage, loading, hasMore, workouts.length, user?.id]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      handleScrollForPagination();
      handleScrollPosition();
    };
    
    // Find the actual scrollable container
    const pageContainer = document.querySelector('.page-container') as HTMLElement;
    
    if (pageContainer) {
      // Attach to the page-container instead of window
      pageContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (pageContainer) {
        pageContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScrollForPagination, handleScrollPosition]);

  const handleExerciseClick = (exerciseId: string) => {
    setExpandedExerciseId(expandedExerciseId === exerciseId ? null : exerciseId);
  };

  return (
    <div className={styles.historyContent} data-testid={testId}>
      <div className="page-content">
        <div className={`content-card ${styles.contentCard}`}>
          {initialLoading ? (
            <div className={styles.fullPageLoadingContainer}>
              <div className={styles.fullPageLoadingSpinner}></div>
              <p className={styles.fullPageLoadingText}>{t('workout.loading.historyDetails')}</p>
            </div>
          ) : (
            <>
              <h1 className="content-title">{t('workout.history.title')}</h1>
              <div 
                ref={historyListRef}
                className={styles.scrollContainer}
              >
                <ul className={styles.historyList}>
                  {workouts.map((workout) => (
                    <li key={workout.id} className={styles.historyItem}>
                      <div className={styles.workoutHeaderRow}>
                        {editingWorkoutId === workout.id && draftWorkout ? (
                          <input
                            className={styles.dateInput}
                            type="date"
                            value={toDateInputValue((draftWorkout as any).date)}
                            onChange={(e) => updateDraftDate(e.target.value)}
                          />
                        ) : (
                          <div className={styles.historyDate}>{new Date(workout.date).toLocaleDateString()}</div>
                        )}

                        <div className={styles.workoutActions}>
                          {editingWorkoutId === workout.id ? (
                            <>
                              <button
                                className={styles.actionButton}
                                onClick={cancelEditingWorkout}
                                disabled={isSavingEdit}
                              >
                                {t('workout.history.cancel')}
                              </button>
                              <button
                                className={styles.actionButton}
                                onClick={saveEditingWorkout}
                                disabled={isSavingEdit}
                              >
                                {isSavingEdit ? t('workout.history.saving') : t('workout.history.save')}
                              </button>
                            </>
                          ) : user ? (
                            <button className={styles.actionButton} onClick={() => startEditingWorkout(workout)}>
                              {t('workout.history.edit')}
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <div className={styles.historyExercises}>
                        {(editingWorkoutId === workout.id && draftWorkout ? draftWorkout.exercises : workout.exercises).map(
                          (exercise, idx) => {
                            const maxAchieved = getMaxAchievedForExercise(exercise.name, workout);
                            const hasAnyReps = exercise.sets.some((set) => set.reps > 0);
                            const isPartial = !exercise.completed && hasAnyReps;

                            const exerciseId = `${workout.id}-${exercise.name}-${idx}`;
                            const isExpanded = expandedExerciseId === exerciseId;

                            let statusClass = styles.skipped;
                            if (exercise.completed) {
                              statusClass = styles.completed;
                            } else if (isPartial) {
                              statusClass = styles.partial;
                            }

                            const exerciseLookupId = (exercise as any).exerciseId || exercise.id;
                            const normalizedExerciseName = normalizeExerciseLookup(exercise.name || '');
                            const exerciseDef =
                              (exerciseLookupId ? exerciseDefsById[exerciseLookupId] : undefined) ||
                              exerciseDefsById[normalizedExerciseName];
                            const displayExerciseName = exerciseDef?.localizedName || exercise.name;
                            const imageKey = exerciseLookupId || normalizedExerciseName || `${workout.id}-${idx}`;
                            const failedSources = failedImageSourcesByExercise[imageKey] || [];
                            const imageCandidates = getResolvedImageCandidates([
                              exerciseDef?.imageUrl,
                              exerciseLookupId ? `/api/exercises/${exerciseLookupId}/image` : undefined,
                            ]);
                            const exerciseImageUrl = imageCandidates.find((url) => !failedSources.includes(url)) || null;

                            return (
                              <div
                                key={idx}
                                className={styles.historyExercise}
                                onClick={() => handleExerciseClick(exerciseId)}
                              >
                                <div className={styles.historyExerciseHeader}>
                                  <span className={`${styles.exerciseStatus} ${statusClass}`}>
                                    {exerciseImageUrl ? (
                                      <img
                                        className={styles.exerciseStatusImage}
                                        src={exerciseImageUrl}
                                        alt={displayExerciseName}
                                        loading="lazy"
                                        onError={() => {
                                          setFailedImageSourcesByExercise((prev) => {
                                            const existing = prev[imageKey] || [];
                                            if (existing.includes(exerciseImageUrl)) return prev;
                                            return {
                                              ...prev,
                                              [imageKey]: [...existing, exerciseImageUrl],
                                            };
                                          });
                                        }}
                                      />
                                    ) : (
                                      <span className={styles.exerciseStatusPlaceholder} />
                                    )}
                                  </span>

                                  <span className={styles.historyExerciseName}>
                                    {displayExerciseName}
                                    {maxAchieved && (
                                      <span className={styles.maxValue}>
                                        {maxAchieved.maxWeightSet && maxAchieved.maxWeightSet.weight > 0 && (
                                          <span className={styles.maxWeight} title={t('exercises.modal.maxWeight')}>
                                            💪 {t('exercises.modal.maxWeight')}: {maxAchieved.maxWeightSet.weight} {maxAchieved.maxWeightSet.unit} ×{' '}
                                            {maxAchieved.maxWeightSet.reps}
                                          </span>
                                        )}
                                        {maxAchieved.maxRepsSet && maxAchieved.maxRepsSet.reps > 0 && (
                                          <span className={styles.maxReps} title={t('exercises.modal.repsForMax')}>
                                            🔥 {t('exercises.modal.repsForMax')}: {maxAchieved.maxRepsSet.weight} {maxAchieved.maxRepsSet.unit} ×{' '}
                                            {maxAchieved.maxRepsSet.reps}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </span>

                                  <span className={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
                                </div>

                                {isExpanded && (
                                  <div className={styles.exerciseDetails}>
                                    <div className={styles.setsList}>
                                      {editingWorkoutId === workout.id && draftWorkout ? (
                                        <>
                                          {(exercise.sets || []).map((set, setIdx) => (
                                            <div key={setIdx} className={styles.setItem}>
                                              <div
                                                className={styles.setEditRow}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <input
                                                  className={styles.setInput}
                                                  type="number"
                                                  value={set.reps}
                                                  onChange={(e) =>
                                                    updateDraftSet(idx, setIdx, { reps: Number(e.target.value) })
                                                  }
                                                />
                                                {/* TODO.md E, item 5: a hold-time set (unit === 'sec') has no weight to
                                                    edit - hide the weight input and unit select rather than showing a
                                                    weight field that's always forced back to 0. */}
                                                {set.unit === 'sec' ? (
                                                  <span className={styles.setEditLabel}>{t('workout.editExercise.holdTimeUnitShort')}</span>
                                                ) : (
                                                  <>
                                                    <span className={styles.setEditLabel}>{t('workout.editExercise.repsPlaceholder')}</span>

                                                    <input
                                                      className={styles.setInput}
                                                      type="number"
                                                      value={set.weight}
                                                      onChange={(e) =>
                                                        updateDraftSet(idx, setIdx, { weight: Number(e.target.value) })
                                                      }
                                                    />
                                                    <select
                                                      className={styles.setSelect}
                                                      value={set.unit}
                                                      onChange={(e) => updateDraftSet(idx, setIdx, { unit: e.target.value })}
                                                    >
                                                      <option value="kg">kg</option>
                                                      <option value="lb">lb</option>
                                                      <option value="bw">bw</option>
                                                      <option value="assist">assist</option>
                                                    </select>
                                                  </>
                                                )}

                                                <button
                                                  className={styles.smallDangerButton}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeDraftSet(idx, setIdx);
                                                  }}
                                                >
                                                  {t('workout.editExercise.remove')}
                                                </button>
                                              </div>
                                            </div>
                                          ))}

                                          <div className={styles.editActionsRow} onClick={(e) => e.stopPropagation()}>
                                            <button
                                              className={styles.smallActionButton}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                addDraftSet(idx);
                                              }}
                                            >
                                              {t('workout.editExercise.addSet')}
                                            </button>
                                            <button
                                              className={styles.smallDangerButton}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                removeDraftExercise(idx);
                                              }}
                                            >
                                              {t('workout.templates.modal.removeExercise')}
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        (exercise.sets || []).map((set, setIdx) => (
                                          <div key={setIdx} className={styles.setItem}>
                                            {set.unit === 'sec' ? (
                                              <span className={styles.setReps}>{t('workout.editExercise.holdTimeValue', { count: set.reps })}</span>
                                            ) : (
                                              <>
                                                <span className={styles.setReps}>{t('workout.editExercise.repsValue', { count: set.reps })}</span>
                                                <span className={styles.setWeight}>
                                                  {set.weight} {set.unit}
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        ))
                                      )}
                                    </div>

                                    {maxAchieved && maxAchieved.maxWeightSet && maxAchieved.maxWeightSet.reps > 0 && (
                                      <div className={styles.exerciseDetailsRow}>
                                        <span className={styles.exerciseLabel}>{t('exercises.modal.personalRecord')}:</span>
                                        <span className={styles.exerciseValue}>
                                          {maxAchieved.maxWeightSet.weight} {maxAchieved.maxWeightSet.unit}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </li>
                  ))}

                  {loading && (
                    <li className={styles.loadingItem}>
                      <div className={styles.loadingIndicator}>{t('workout.loading.history')}</div>
                    </li>
                  )}

                  {!hasMore && workouts.length > 0 && (
                    <li className={styles.endItem}>
                      <div className={styles.endIndicator}>{t('workout.history.noMore', { defaultValue: 'No more workouts to load' })}</div>
                    </li>
                  )}

                  {workouts.length === 0 && !loading && (
                    <li className={styles.emptyItem}>
                      <div className={styles.emptyIndicator}>{t('workout.history.empty', { defaultValue: 'No workout history found' })}</div>
                    </li>
                  )}
                </ul>
              </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};
export default WorkoutHistory;
